import React from 'react';
import { ShieldOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '../lib/security';

interface NoAccessPageProps {
    /**
     * 'denied' - the API answered, and this person holds nothing.
     * 'error'  - the API did not answer, so entitlement is unknown.
     */
    variant?: 'denied' | 'error';
}

/**
 * Shown to someone who signed in successfully but cannot be let in.
 *
 * Signing in proves who they are; it does not entitle them to anything. This
 * screen exists so that reads as a deliberate answer rather than an empty app
 * or a wall of failed requests - and so a failure to ask the question is not
 * reported as a refusal, which is how a cold start once told an administrator
 * they had no access.
 */
const NoAccessPage: React.FC<NoAccessPageProps> = ({ variant = 'denied' }) => {
    const { userInfo, logout, refreshAccess } = useAuth();
    const isError = variant === 'error';

    return (
        <div className="bx-wrap py-16 fade-in">
            <div className="mx-auto max-w-lg bg-surface rounded-xl border border-gray-200 p-8 text-center">
                <span className={`inline-flex w-12 h-12 rounded-xl items-center justify-center mb-4 ${
                    isError ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-400'
                }`}>
                    {isError ? <AlertTriangle className="w-6 h-6" /> : <ShieldOff className="w-6 h-6" />}
                </span>

                <h1 className="text-2xl font-bold text-gray-800">
                    {isError ? 'Could not check your access' : 'No access yet'}
                </h1>

                <p className="text-gray-500 mt-3 leading-relaxed">
                    {isError ? (
                        <>
                            BIKI could not reach the service that says what you are allowed to
                            see, so this is not a decision about your account. It is usually
                            momentary - try again.
                        </>
                    ) : (
                        <>
                            You are signed in{userInfo?.email ? ` as ${userInfo.email}` : ''}, but
                            your account has not been granted access to BIKI. Ask an administrator
                            to add you, then reload this page.
                        </>
                    )}
                </p>

                <div className="mt-6 flex items-center justify-center gap-3">
                    <button
                        onClick={() => (isError ? void refreshAccess() : window.location.reload())}
                        className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm font-medium"
                    >
                        {isError ? 'Try again' : 'Reload'}
                    </button>
                    <button
                        onClick={logout}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NoAccessPage;
